import Turnstile from "../../components/auth/Turnstile";
import { env } from "../../env";
import { ReactComponent as Logo } from "../../vendor/logo_black.svg";
import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Alert as MuiAlert,
  TextField as MuiTextField,
  Paper,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Formik } from "formik";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";
import * as Yup from "yup";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Brand = styled(Logo)`
  fill: ${(props) => props.theme.palette.primary.main};
  width: 250px;
  margin-bottom: 32px;
`;

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)};

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)};
  }
`;

const ForgotPassword = () => {
  return (
    <>
      <Brand />
      <Wrapper>
        <Helmet title="Forgot Password" />

        <ForgotPasswordForm />
      </Wrapper>
    </>
  );
};

export const SEND_PASSWORD_RESET_EMAIL_MUTATION = gql`
  mutation SendPasswordResetEmail($email: String!, $turnstileToken: String) {
    sendPasswordResetEmail(email: $email, turnstileToken: $turnstileToken) {
      success
      errors
    }
  }
`;

const ForgotPasswordForm = () => {
  const [sendPasswordResetEmail, { data, loading, error }] = useMutation(
    SEND_PASSWORD_RESET_EMAIL_MUTATION
  );
  const turnstileEnabled = Boolean(env.REACT_APP_TURNSTILE_SITE_KEY);
  // A Turnstile token is single use, and the API spends it on every request.
  // A new key remounts the widget so the next request gets a fresh one.
  const [turnstileKey, setTurnstileKey] = useState(0);

  return (
    <>
      {loading && (
        <Alert my={2} severity="info">
          Handling your request...
        </Alert>
      )}
      {error && (
        <Alert my={2} severity="warning">
          Sorry, there was an error. Please contact us at{" "}
          <a href="mailto:support@podium.live">support@podium.live</a>.
        </Alert>
      )}
      {data?.sendPasswordResetEmail?.success && (
        <Alert my={2} severity="success">
          Please check your inbox for an email that has the next steps to reset
          your password.
        </Alert>
      )}

      <Typography component="h2" variant="body1" align="center">
        Enter your email address to reset your password
      </Typography>

      <Formik
        initialValues={{
          email: "",
          submit: false,
          turnstileToken: "",
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email("Must be a valid email")
            .max(255)
            .required("Email is required"),
          turnstileToken: turnstileEnabled
            ? Yup.string().required("Captcha verification is required")
            : Yup.string(),
        })}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (
          values,
          { setErrors, setFieldValue, setStatus, setSubmitting }
        ) => {
          try {
            const result = await sendPasswordResetEmail({
              variables: {
                email: values.email,
                turnstileToken: values.turnstileToken || null,
              },
            });
            const response = result.data?.sendPasswordResetEmail;
            if (!response?.success) {
              const errors = response?.errors;
              console.error("Password reset email was refused", errors);
              setStatus({ success: false });
              setErrors({
                submit:
                  errors?.turnstileToken?.[0]?.message ||
                  errors?.email?.[0]?.message ||
                  errors?.nonFieldErrors?.[0]?.message ||
                  "Something went wrong",
                turnstileToken: errors?.turnstileToken
                  ? "Please verify again."
                  : undefined,
              });
              setSubmitting(false);
            }
          } catch (error) {
            const message = error.message || "Something went wrong";

            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          } finally {
            setFieldValue("turnstileToken", "", false);
            setTurnstileKey((key) => key + 1);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            {errors.submit && (
              <Alert mt={2} mb={1} severity="warning">
                {errors.submit}
              </Alert>
            )}
            <TextField
              autoFocus
              type="email"
              name="email"
              label="Email Address"
              value={values.email}
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              onBlur={handleBlur}
              onChange={handleChange}
              my={3}
              required
            />
            <Turnstile
              key={turnstileKey}
              error={errors.turnstileToken}
              onTokenChange={(token) => setFieldValue("turnstileToken", token)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              Reset password
            </Button>
            <Button
              component={Link}
              to="/auth/sign-in"
              fullWidth
              color="primary"
            >
              Cancel
            </Button>
          </form>
        )}
      </Formik>
    </>
  );
};

export default ForgotPassword;
